import type { AxiosResponse } from 'axios';
import type { SagaIterator } from 'redux-saga';
import { call, select, take } from 'redux-saga/effects';

import { resourceSagaUtils } from '../core';
import type { Meta } from '../core/actionHelpers';
import { featureFlagsSelectors } from '../feature-flags';
import * as actions from './investmentQuotesV2Actions';
import { getLastSyncDate } from './investmentQuotesV2Selectors';
import {
  type InvestmentQuotesV2Response,
  mkInvestmentQuotesV2Payload,
  mkInvestmentQuoteV2,
} from './investmentQuotesV2Types';

export function* getInvestmentQuotesActionWatcher(): SagaIterator {
  while (true) {
    const action: ReturnType<typeof actions.getInvestmentQuotesV2> = yield take(actions.getInvestmentQuotesV2.type);
    const featureFlag: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'realTimeQuotes');
    const originalMeta = action.meta || {};
    const payloadSymbolsParam = action.payload?.symbols;
    const symbolsParam = payloadSymbolsParam ? payloadSymbolsParam : originalMeta.axiosConfig?.params?.symbols;
    const lastSyncDate: ReturnType<typeof getLastSyncDate> = yield select(getLastSyncDate);
    const symbols = Array.isArray(symbolsParam)
      ? symbolsParam
      : typeof symbolsParam === 'string' && symbolsParam.length > 0
        ? symbolsParam.split(',')
        : [];

    yield call(
      resourceSagaUtils.qcsRequest,
      {
        method: 'POST',
        path: '/v2/investments/quotes',
        data: { symbols },
        params: {
          realTime: featureFlag,
          includeFallback: true,
          ...(lastSyncDate && { modifiedAfter: lastSyncDate }),
        },
        showError: false,
        successAction: (response?: AxiosResponse<InvestmentQuotesV2Response>, meta?: Meta) =>
          actions.getInvestmentQuotesV2Success(
            mkInvestmentQuotesV2Payload({
              resources:
                response?.data?.resources?.map((data) =>
                  mkInvestmentQuoteV2({
                    ...data,
                    id: data.relatedSymbol || data.symbol, // relatedSymbol is unique, but symbol can collide e.g. ^ETHUSD=ETH(crypto) ETH(etf)
                  }),
                ) || [],
              lastSyncDate: response?.data?.metaData?.asOf || null,
            }),
            meta,
          ),
        failureAction: actions.getInvestmentQuotesV2Failure,
      },
      action,
    );
  }
}

export default [getInvestmentQuotesActionWatcher];
